// src/hooks/usePendingInviteWatcher.js
import { useEffect } from "react";
import { auth, db, functions } from "../firebase";
import { doc, onSnapshot } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { emitToast } from "../components/Toast";

/**
 * Watches a pending invite doc in Firestore and auto-accepts it
 * the moment the admin sets approved=true.
 *
 * Stored in localStorage as:
 *   fv_pendingInvite = JSON { token, vaultId, inviteId }
 *
 * Only active when the user is authenticated (called from PresenceAndLayout).
 */
export function usePendingInviteWatcher() {
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    const raw = localStorage.getItem("fv_pendingInvite");
    if (!raw) return;

    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch {
      localStorage.removeItem("fv_pendingInvite");
      return;
    }

    const { token, vaultId, inviteId } = parsed;
    if (!token || !vaultId || !inviteId) return;

    const inviteRef = doc(db, `vaults/${vaultId}/invites/${inviteId}`);

    const unsubscribe = onSnapshot(
      inviteRef,
      async (snap) => {
        if (!snap.exists()) {
          // Invite was deleted (declined or expired)
          localStorage.removeItem("fv_pendingInvite");
          emitToast("Your invite is no longer available.", "error", 5000);
          return;
        }

        const data = snap.data();

        // Still waiting
        if (!data.approved || data.needsAdminReview !== false) return;

        // Already used — clear and bail
        if (data.used) {
          localStorage.removeItem("fv_pendingInvite");
          return;
        }

        // Admin approved — auto-accept
        try {
          const acceptInvite = httpsCallable(functions, "acceptInvite");
          const result = await acceptInvite({ token });

          if (result.data?.success) {
            localStorage.removeItem("fv_pendingInvite");
            emitToast("You've been approved! Welcome to the vault.", "success", 6000);
            // Reload to fetch updated vault list
            window.location.reload();
          }
        } catch (err) {
          console.error("usePendingInviteWatcher: auto-accept failed", err);
          emitToast(
            "Your invite was approved but joining failed. Please click your invite link again.",
            "error",
            7000
          );
        }
      },
      (err) => {
        // permission-denied means the invite doc doesn't match the user's email
        // or was removed — clear storage silently
        console.warn("usePendingInviteWatcher: snapshot error", err.code);
        if (err.code === "permission-denied") {
          localStorage.removeItem("fv_pendingInvite");
        }
      }
    );

    return () => unsubscribe();
  }, []);
}
